import { cloneDeep } from "lodash";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { Option } from "../../../common/types/Option";
import { BudgetType } from "../../../common/types/roots/fields";
import { report127Expenses, report127FilterOptions, report127Indicators } from "../report-127";
import { Report127FilterSettingsForm } from "../settings-form/Report127FilterSettingsForm";
import { Report127FilterSettings } from "../settings/Report127FilterSettings";
import { Report127TableDataRequest } from "../other-types/Report127TableDataRequest";
import { getLastDateOfMonth } from "../../../common/utils/dateUtils";
import { allIncomeClassificators } from "../../../common/baseOptions";
import { Report127JournalRequest } from "../journal/Report127JournalRequest";
import { Report127Row, Report127TableRow, SectionSeparator } from "../table-data/table-data-types";
import i18nService from "@/services/i18n";

export class Report127Facade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(report127FilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидаций
        if (settings.reportName !== '1-27') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== '1-27') {
            throw new Error('error_type_filter_options')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }

        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        dataSource = { ...dataSource }

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        periodicity = { ...periodicity }

        const regions: Option<string>[] = []
        settings.regions.forEach(region => {
            const option = options.regions.find(it => it.value === region)!
            regions.push({...option})
        })

        let mutuallyRedeemingOption = options.mutuallyRedeeming.find(it => it.value === settings.mutuallyRedeeming)
        if (!mutuallyRedeemingOption) {
            throw new Error('error_not_found_mutually_redeeming_option')
        }
        mutuallyRedeemingOption = { ...mutuallyRedeemingOption }
        
        // use ?? for backwards compatability
        const mutuallyRedeemingLevels = settings.mutuallyRedeemingLevels ?? []
        const mutuallyRedeemingLevelOptions = cloneDeep(
            options.mutuallyRedeemingLevels.filter(it => mutuallyRedeemingLevels.includes(it.value))
        )

        const budgetTypes: Option<BudgetType>[] = options.budgetTypes.filter(it => settings.budgetTypes.includes(it.value))

        let measureUnit = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnit) {
            throw new Error('error_not_found_measure_unit')
        }
        measureUnit = { ...measureUnit }

        let budgetStructure = options.budgetStructures.find(item => item.value == settings.budgetStructure) ?? null
        if (budgetStructure) {
            budgetStructure = {...budgetStructure}
        }

        let developType = options.developTypes.find(it => it.value === settings.developType)
        if (!developType) {
            throw new Error('error_not_found_develop_type')
        }
        developType = { ...developType }

        let month = options.months.find(it => it.value == settings.month) ?? null
        if (month) {
            month = { ...month }
        }

        let isRoundUp: boolean = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }

        let expenseType = options.expenseTypes.find(it => it.value == settings.expenseType) ?? null
        if (!expenseType) {
            throw new Error('error_not_found_expense_type')
        }
        expenseType = { ...expenseType }

        // 2. Формирование
        const form: Report127FilterSettingsForm = {
            key: '1-27',

            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,

            regions: regions,
            mutuallyRedeeming: mutuallyRedeemingOption,
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            budgetTypes: budgetTypes,
            measureUnit: measureUnit,
            budgetStructure: budgetStructure,
            developType: developType,
            year: settings.year,
            month: month,
            separateByRegion: settings.separateByRegion,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo ?? null,
            isTree: settings.isTree ?? true, // Для обратной совместимости шаблонов сохраненных до добавления этого свойства
            incomeTotal: settings.incomeTotal,
            expenseTotal: settings.expenseTotal,
            msuAggregation: settings.msuAggregation ?? false,

            incomes: cloneDeep(settings.incomes),
            expenseType: expenseType,
            expenses: cloneDeep(settings.expenses),
            indicators: cloneDeep(settings.indicators)
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== '1-27') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (form.mutuallyRedeeming == null) {
            throw new Error('error_input_mutually_redeeming')
        }
        if (form.measureUnit == null) {
            throw new Error('error_input_measure_unit')
        }
        if (form.budgetStructure == null) {
            throw new Error('error_input_budget_struct')
        }
        if (form.developType == null) {
            throw new Error('error_input_develop_type ')
        }
        if (form.year == null) {
            throw new Error('error_input_year')
        }
        if (form.month == null) {
            throw new Error('error_input_month')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const settings: Report127FilterSettings = {
            reportName: '1-27',

            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,

            regions: form.regions.map(it => it.value),
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels.map(it => it.value),
            budgetTypes: form.budgetTypes.map(it => it.value),
            measureUnit: form.measureUnit.value,
            budgetStructure: form.budgetStructure.value,
            developType: form.developType.value ?? null,
            year: form.year,
            month: form.month.value,
            separateByRegion: form.separateByRegion,
            roundUpTo: roundUpTo,
            isTree: form.isTree,
            incomeTotal: form.incomeTotal,
            expenseTotal: form.expenseTotal,
            msuAggregation: form.msuAggregation ?? false,
            incomes: cloneDeep(form.incomes),
            expenseType: form.expenseType.value,
            expenses: cloneDeep(form.expenses),
            indicators: cloneDeep(form.indicators)
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(report127FilterOptions)

        const emptyForm: Report127FilterSettingsForm = {
            key: '1-27',

            reportName: options.reportNames.find(it => it.value === '1-27')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'MIXED')!,
            dataSource: options.dataSources.find(it => it.value === 'LOADER')!,
            periodicity: options.periodicities.find(it => it.value === 'MONTH')!,

            regions: [],
            mutuallyRedeeming: options.mutuallyRedeeming.find(it => it.value === 'WITH')!,
            mutuallyRedeemingLevels: options.mutuallyRedeemingLevels,
            budgetTypes: [],
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            budgetStructure: null,
            developType: options.developTypes.find(it => it.value === null)!,
            year: (new Date().getFullYear()),
            month: null,
            separateByRegion: false,
            roundUp: false,
            roundUpTo: null,
            isTree: true,
            incomeTotal: false,
            expenseTotal: false,
            msuAggregation: false,

            incomes: cloneDeep(allIncomeClassificators),
            expenses: cloneDeep(report127Expenses),
            expenseType: options.expenseTypes.find(it => it.value === 'FUNCTIONAL')!,
            indicators: cloneDeep(report127Indicators)
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций
        if (form.key !== '1-27') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (!form.year || !form.month) {
            throw new Error('error_input_year_month')
        }

        if ('regions' in form && form.regions.length === 0) {
            throw new Error('error_input_region')
        }
        if (form.mutuallyRedeeming == null) {
            throw new Error('error_input_mutually_redeeming')
        }

        if (form.developType == null) {
            throw new Error('error_input_develop_type ')
        }
        const developType = form.developType.value


        if (form.measureUnit == null) {
            throw new Error('error_input_measure_unit')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }
        const mutually_redeeming_levels = form.mutuallyRedeemingLevels.map(it => {
            if (it.value === 'TYPE-3') {
                return 3
            } else if (it.value === 'TYPE-4') {
                return 4
            } else {
                throw new Error('error_unsupported_vzk_level')
            }
        })
        // 2. Формирование данных для таблицы
        const tempIncomes = form.incomes
            .map(it => ({ key: it.key, selected: it.selected, active: it.active }))
        const tempExpenses = form.expenses
            .map(it => ({ key: it.key, selected: it.selected, active: it.active }))
        const params: Report127TableDataRequest = {
            incomes: tempIncomes,
            expenses: tempExpenses,
            expense_type: form.expenseType.value,
            year: form.year,
            month: form.month.value,
            regions: form.regions.map(it => it.value),
            budget_types: form.budgetTypes.map(it => it.value),
            measure_unit: form.measureUnit.value,
            round_up_to: roundUpTo,
            is_tree: form.isTree,
            mutually_redeeming: form.mutuallyRedeeming.value,
            mutually_redeeming_levels: mutually_redeeming_levels,
            budget_structure: form.budgetStructure?.value ?? null,
            develop_type: developType,
            income_total: form.incomeTotal,
            expense_total: form.expenseTotal,
            separate_by_region: form.separateByRegion,
            msu_aggregation: form.msuAggregation ?? false
        }
        const response = await fetch('/api-py/monitoring/reports-constructor/1-27',
            {
                method: 'POST',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }
        const responseTableData = await response.json()

        let index = 0
        // functions are defined in this scope to use 'index' variable for unique keys
        const mapRow = (item: any): Report127TableRow => {
            const mapped: Report127TableRow = {
                index: index++,

                gr: item.gr ?? null,
                pgr: item.pgr ?? null,
                abp: item.abp ?? null,
                prg: item.prg ?? null,
                ppr: item.ppr ?? null,
                epcl: item.epcl ?? null,
                espf: item.espf ?? null,

                kat: item.kat ?? null,
                cls: item.cls ?? null,
                pcl: item.pcl ?? null,
                spf: item.spf ?? null,

                nameRu: item.name_ru || null,
                nameKk: item.name_kk || null,

                utv: roundToDecimal(item.utv ?? null, roundUpTo),
                utch: roundToDecimal(item.utch ?? null, roundUpTo),
                plg: roundToDecimal(item.plg ?? null, roundUpTo),
                plgp: roundToDecimal(item.plgp ?? null, roundUpTo),
                plgo: roundToDecimal(item.plgo ?? null, roundUpTo),
                obz: roundToDecimal(item.obz ?? null, roundUpTo),
                nobz: roundToDecimal(item.nobz ?? null, roundUpTo),
                sumrg: roundToDecimal(item.sumrg ?? null, roundUpTo),
                percentage1: item.percentage1 ?? null,
                percentage2: item.percentage2 ?? null,

                bold: item.bold
            }
            return mapped
        }

        const tempTableData: Report127Row[] = []

        responseTableData.forEach((it: any) => {
            if (it.separator) {
                let nameRu: string = it.name_ru
                let nameKk: string = it.name_kk
                if (nameRu === 'V. ДЕФИЦИТ (ПРОФИЦИТ) БЮДЖЕТА') {
                    nameRu = 'V. ДЕФИЦИТ (ПРОФИЦИТ) БЮДЖЕТА \n VI. НЕНЕФТЯНОЙ ДЕФИЦИТ (ПРОФИЦИТ) БЮДЖЕТА \n VII. ФИНАНСИРОВАНИЕ ДЕФИЦИТА (ИСПОЛЬЗОВАНИЕ ПРОФИЦИТА) БЮДЖЕТА \n Справочно: Остатки бюджетных средств'
                    nameKk = 'V. БЮДЖЕТ ТАПШЫЛЫҒЫ (ПРОФИЦИТІ) \n VI. БЮДЖЕТТІҢ МҰНАЙЛЫҚ ЕМЕС ТАПШЫЛЫҒЫ (ПРОФИЦИТІ) \n VII. БЮДЖЕТ ТАПШЫЛЫҒЫН ҚАРЖЫЛАНДЫРУ (ПРОФИЦИТІН ПАЙДАЛАНУ) \n Анықтама үшін: Бюджет қаражаттарының қалдықтары'
                }

                const separatorType: 'SECTION' | 'REGION' = it.separator

                const separator: SectionSeparator = { nameRu: nameRu, nameKk: nameKk, type: separatorType }
                tempTableData.push(separator)
            }
            const onlySeparator = it.utv === undefined
            if (!onlySeparator) {
                const mapped = mapRow(it)
                tempTableData.push(mapped)
            }
        })

        // 3. Формирование параметров для выгрузки журнал
        const date = getLastDateOfMonth(form.year, form.month.value)
        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const excludeEmpty: boolean = !form.isTree
        const regionLabels: string[][] = form.regions.map(it => [it.label, (it.labelKk || it.label)])
        const journalParams: Report127JournalRequest = {
            reportName: "1-27",
            // journal data
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            // for excel
            incomes_order: form.incomes.filter(it => it.active).map(it => it.key),
            expenses_order: form.expenses.filter(it => it.active).map(it => it.key),
            expense_type: form.expenseType.value,
            income_total: form.incomeTotal,
            expense_total: form.expenseTotal,
            indicators_order: form.indicators.filter(it => it.active).map(it => it.key),
            table_data: [],
            data_request: params,
            date: date,
            region_labels: regionLabels,
            mutually_redeeming: form.mutuallyRedeeming.value,
            separate_by_region: form.separateByRegion,
            budget_types: form.budgetTypes.map(it => it.value),
            measure_unit: form.measureUnit.value,
            round_up: roundUpTo,
            locale: i18nService.locale,
            exclude_empty: excludeEmpty,
            excel_version: 'TC', // hard-coded here but is meant to be set dynamically when manually chosen in download options
        }

        const result: TableDataAndJournalParams = {
            tableData: tempTableData,
            journalParams: journalParams
        }

        return result
    }
}

const roundToDecimal = (value: number | null, scale: number | null): number | null => {
    if (scale == null || value == null) {
        return value
    }
    const factor = Math.pow(10, scale)
    return Math.round((value + Number.EPSILON) * factor) / factor
}
